/** Posters work without JavaScript; video bytes are requested only near the viewport. */
export function initVideoPreviews(): void {
  const roots = document.querySelectorAll<HTMLElement>('[data-video-preview]');
  if (!roots.length || !('IntersectionObserver' in window)) return;

  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  let pageHidden = false;
  const previews = new Map<HTMLElement, {
    video: HTMLVideoElement;
    near: boolean;
    visible: boolean;
    loaded: boolean;
    pending: boolean;
    failed: boolean;
  }>();

  const allowed = (): boolean => !motion.matches && !document.hidden && !pageHidden;

  const update = (root: HTMLElement): void => {
    const state = previews.get(root);
    if (!state) return;
    const { video } = state;

    if (!allowed() || state.failed || !state.visible) {
      video.pause();
      root.classList.remove('is-playing');
    }
    if (!allowed() || state.failed || !state.near) return;

    if (!state.loaded) {
      video.src = video.dataset['src']!;
      delete video.dataset['src'];
      state.loaded = true;
      video.preload = 'auto';
      video.load();
    }
    if (!state.visible || state.pending || !video.paused) return;

    state.pending = true;
    void video.play().then(() => {
      // Visibility or motion preference can change while play() is pending.
      if (!allowed() || !state.visible || state.failed) {
        video.pause();
        root.classList.remove('is-playing');
      }
    }).catch((error: unknown) => {
      // pause() can interrupt a pending play request during scrolling.
      if (error instanceof DOMException && error.name === 'AbortError') return;
      state.failed = true;
      video.pause();
      root.classList.remove('is-playing');
      console.warn('[video-preview] Playback unavailable; keeping the poster.', error);
    }).finally(() => {
      state.pending = false;
      // A paused request may have been aborted just as the card re-entered view.
      if (allowed() && state.visible && !state.failed && video.paused) update(root);
    });
  };

  const nearObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const root = entry.target as HTMLElement;
      const state = previews.get(root);
      if (!state) continue;
      state.near = entry.isIntersecting;
      update(root);
    }
  }, { rootMargin: '200px 0px' });

  const visibleObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const root = entry.target as HTMLElement;
      const state = previews.get(root);
      if (!state) continue;
      state.visible = entry.isIntersecting;
      update(root);
    }
  });

  for (const root of roots) {
    const video = root.querySelector<HTMLVideoElement>('video');
    if (!video) continue;
    video.muted = true;
    previews.set(root, { video, near: false, visible: false, loaded: false, pending: false, failed: false });
    video.addEventListener('playing', () => {
      const state = previews.get(root)!;
      if (allowed() && state.visible && !state.failed) root.classList.add('is-playing');
    });
    for (const event of ['pause', 'waiting', 'ended']) {
      video.addEventListener(event, () => root.classList.remove('is-playing'));
    }
    const fail = (): void => {
      const state = previews.get(root)!;
      if (!state.loaded || state.failed) return;
      state.failed = true;
      update(root);
      console.warn('[video-preview] Video could not load; keeping the poster.');
    };
    video.addEventListener('error', fail);
    nearObserver.observe(root);
    visibleObserver.observe(root);
  }

  const updateAll = (): void => previews.forEach((_, root) => update(root));
  motion.addEventListener('change', updateAll);
  document.addEventListener('visibilitychange', updateAll);
  addEventListener('pagehide', () => { pageHidden = true; updateAll(); });
  addEventListener('pageshow', () => { pageHidden = false; updateAll(); });
}
